import { useEffect, useState, useCallback, useContext } from "react";

import { useSelector } from "react-redux";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../shadcn/components/ui/popover";
import { Button } from "../../../shadcn/components/ui/button";
import ChevronUpSvg from "../../../icons/ChevronUpSvg";
import ChevronDownSvg from "../../../icons/ChevronDownSvg";
import CalendarDotsIcon from "../../../icons/CalendarDotsIcon";
import { getBankerDashboardEventsCount } from "services";
import CalendarDoubleDatePicker from "./CalendarDoubleDatePicker";
import { LoadingSpinner } from "../../../icons/LoadingSpinner";

interface EventDataCardProps {
  title: string;
  count: number;
}

interface AuctionOverviewComponentProps {
  outerContainerClass?: string;
}

export const AuctionOverviewComponent = ({
  outerContainerClass,
}: AuctionOverviewComponentProps) => {
  const [selectedRange, setSelectedRange] = useState("this_month");
  const [customStartDate, setCustomStartDate] = useState<Date | null>(null);
  const [customEndDate, setCustomEndDate] = useState<Date | null>(null);

  const [events, setEvents] = useState<any>([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  const [isStartDateOpen, setIsStartDateOpen] = useState(false);
  const [categorizedEvents, setCategorizedEvents] = useState<any>({});

  const currentBranch = useSelector((state: any) => {
    return state.bankerAuth.currentBranch;
  });

  const formatDate = (date: Date | undefined) => {
    if (!date) return "";

    const options: Intl.DateTimeFormatOptions = {
      day: "2-digit",
      month: "short",
      year: "numeric",
    };
    const formattedDate = new Intl.DateTimeFormat("en-GB", options).format(
      date,
    ); // Use 'en-GB' for correct order

    return formattedDate.replace(/(\d{2}) (\w{3}) (\d{4})/, "$1 $2,$3"); // Rearranging to '17 Feb, 2025'
  };

  const formatTitle = (key: string) => {
    return key
      .replace(/_/g, " ") // Replace underscores with spaces
      .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize first letter of each word
  };

  function getDateRange(option: string) {
    const today = new Date();

    let startDate, endDate;

    switch (option) {
      case "this_month":
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;

      case "this_financial_year":
        startDate = new Date(today.getFullYear(), 3, 1); // April 1st (Month 3)
        endDate = new Date(today.getFullYear() + 1, 2, 31); // March 31st (Month 2)

        // Adjust if the current month is before April
        if (today.getMonth() < 3) {
          startDate.setFullYear(today.getFullYear() - 1);
          endDate.setFullYear(today.getFullYear());
        }
        break;

      case "last_financial_year":
        startDate = new Date(today.getFullYear() - 2, 3, 1); // April 1st, two years ago
        endDate = new Date(today.getFullYear() - 1, 2, 31); // March 31st, last year

        // Ensure endDate is correctly set to March 31st of last year
        if (endDate.getDate() !== 31) {
          endDate = new Date(today.getFullYear() - 1, 2, 0); // Last day of March of last year
        }
        break;

      case "custom":
        startDate = customStartDate; // April 1st of last year
        endDate = customEndDate; // March 31st of this year
        break;

      default:
        return null;
    }

    return {
      startDate: startDate
        ? new Date(
            Date.UTC(
              startDate.getFullYear(),
              startDate.getMonth(),
              startDate.getDate(),
              0,
              0,
              0,
              0,
            ),
          ).toISOString()
        : "", // Set to start of the day
      endDate: endDate
        ? new Date(
            Date.UTC(
              endDate.getFullYear(),
              endDate.getMonth(),
              endDate.getDate(),
              23,
              59,
              59,
              999,
            ),
          ).toISOString()
        : "", // Set to end of the day
    };
  }

  useEffect(() => {
    try {
      if (
        !["this_month", "this_financial_year", "last_financial_year"].includes(
          selectedRange,
        ) &&
        (!customStartDate || !customEndDate)
      ) {
        setSelectedRange("this_month");
        return;
      }

      if (customStartDate && customEndDate) {
        setIsStartDateOpen(false);
      }

      setEventsLoading(true);
      const dateRange = getDateRange(selectedRange);

      if (dateRange) {
        const fetchResult = async () => {
          const payload = {
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
          };
          const res: any = await getBankerDashboardEventsCount(payload);
          if (res) {
            setEvents(res.data);
            setEventsLoading(false);
          }
        };
        fetchResult();
      }
    } catch (err) {
      console.log("error", err);
    }
  }, [selectedRange, customEndDate, customStartDate, currentBranch]);

  const EventDataCard = ({ title, count }: EventDataCardProps) => {
    return (
      <div className="flex mobile:w-[125px] bg-brand-lightBlue space-x-2 justify-between px-[12px] py-2 items-center">
        <p className="font-openSans font-normal text-web-body-sm leading-4 text-brand-textGrey">
          {title}
        </p>
        <p className="font-openSans font-semibold text-[20px] leading-7">
          {eventsLoading ? (
            <LoadingSpinner />
          ) : count === 0 || count == undefined ? (
            "-"
          ) : (
            count
          )}
        </p>
      </div>
    );
  };

  const auctionStatus = [
    {
      title: "Total Auction",
      count: events?.["totalEvents"] || 0,
    },
    {
      title: "Successful Auction",
      count: events?.["success"] ?? 0,
    },
    { title: "Upcoming Auction", count: events?.["accepted"] ?? 0 },
    { title: "Cancelled Auction", count: events?.["cancelled"] ?? 0 },
  ];

  const dateOptions = [
    "this_month",
    "this_financial_year",
    "last_financial_year",
  ];

  return (
    <div className={` px-4 py-2.5 bg-brand-lightPink  ${outerContainerClass}`}>
      <div className="flex justify-between items-center">
        <p className="font-openSans font-semibold text-web-body-md leading-5">
          Auction Overview
        </p>
        <Popover onOpenChange={setIsStartDateOpen} open={isStartDateOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="flex gap-2 items-center justify-between text-sm rounded-[8px] font-openSans text-brand-textLightGrey p-2"
            >
              <CalendarDotsIcon width={16} height={16} />
              <p className="font-openSans truncate max-w-[70px] mobile:max-w-[120px] sm:max-w-[200px]">
                {customStartDate && customEndDate
                  ? `${formatDate(customStartDate)} - ${formatDate(
                      customEndDate,
                    )}`
                  : formatTitle(selectedRange)}
              </p>
              <div className="px-1 lg:px-2">
                {isStartDateOpen ? (
                  <ChevronUpSvg width={14} height={14} fill={"#7D7D7D"} />
                ) : (
                  <ChevronDownSvg width={14} height={14} fill={"#7D7D7D"} />
                )}
              </div>
            </Button>
          </PopoverTrigger>

          <PopoverContent className="bg-white mr-4">
            <ul>
              {dateOptions.map((item: any, index) => (
                <li
                  key={index}
                  onClick={() => {
                    setSelectedRange(item);
                    setCustomEndDate(null);
                    setCustomStartDate(null);
                    setIsStartDateOpen(false);
                  }}
                  className={`hover:bg-brand-bgLightGrey hover:cursor-pointer text-web-body-lg font-openSans text-brand-textBlack  pl-4 py-2`}
                >
                  {formatTitle(item)}
                </li>
              ))}
              <li
                key={dateOptions.length}
                onClick={() => {
                  // setIsStartDateOpen(false);
                  setSelectedRange("custom");
                }}
                className="hover:bg-brand-bgLightGrey"
              >
                {/* Start Date- End Date */}
                <CalendarDoubleDatePicker
                  startDate={customStartDate}
                  endDate={customEndDate}
                  setStartDate={setCustomStartDate}
                  setEndDate={setCustomEndDate}
                />
              </li>
            </ul>
          </PopoverContent>
        </Popover>
      </div>
      <div className="grid grid-cols-2 md:flex gap-x-7 gap-y-4 md:gap-y-0 md:justify-between w-full mt-2.5">
        {auctionStatus?.map((item: any, index) => (
          <EventDataCard key={index} title={item?.title} count={item?.count} />
        ))}
      </div>
    </div>
  );
};

export default AuctionOverviewComponent;
